// BannerLogoSection.js — Banner de portada: imagen de fondo (con parallax
// opcional), velo de color y un logo centrado encima.
//
// Pensado para el patrón "header flotante + banner": el header va fijo arriba
// con fondo transparente/píldora y este banner queda debajo, ocupando el ancho
// completo. Todo el contenido (logo, velo, altura, línea inferior) se configura
// desde el admin y admite variantes por país/perfil (ver `audiencia.js`), que es
// como un mismo banner muestra una marca distinta según quién mire.
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { BACKEND_BASE } from '../../../services/api';
import { resolveDeviceValue } from '../../admin/DeviceTabsSelect';

const Banner = styled.section`
  position: relative;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: ${p => p.$alignV || 'center'};
  justify-content: ${p => p.$alignH || 'center'};
  min-height: ${p => p.$alto || '380px'};
  padding: ${p => p.$padding || '4rem 1.5rem'};
  box-sizing: border-box;
  @media (max-width: 991px) and (min-width: 601px) {
    min-height: ${p => p.$altoTablet || p.$alto || '300px'};
    padding: ${p => p.$paddingTablet || p.$padding || '3.5rem 1.25rem'};
  }
  @media (max-width: 600px) {
    min-height: ${p => p.$altoMobile || p.$altoTablet || p.$alto || '240px'};
    padding: ${p => p.$paddingMobile || p.$paddingTablet || p.$padding || '3rem 1rem'};
  }
`;

/* Capa de imagen aparte del contenedor: así el parallax (attachment: fixed) y el
   velo de color no pelean por la misma propiedad `background`. */
const Fondo = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${p => p.$url});
  background-size: ${p => p.$size || 'cover'};
  background-position: ${p => p.$pos || 'center'};
  background-repeat: no-repeat;
  /* El parallax solo en escritorio: en iOS/Android el attachment fijo provoca
     saltos y reencuadres al hacer scroll. Y nunca para quien pidió menos
     movimiento en su sistema. */
  ${p => p.$parallax && `
    @media (min-width: 992px) and (prefers-reduced-motion: no-preference) {
      background-attachment: fixed;
    }
  `}
`;

const Velo = styled.div`
  position: absolute;
  inset: 0;
  background: ${p => p.$overlay};
  ${p => p.$mix && `mix-blend-mode: ${p.$mix};`}
`;

const Contenido = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: ${p => p.$max || '1200px'};
  display: flex;
  flex-direction: column;
  align-items: ${p => p.$alignH || 'center'};
  gap: ${p => p.$gap || '1rem'};
  text-align: ${p => p.$textAlign || 'center'};
`;

const LogoImg = styled.img`
  display: block;
  margin: ${p => p.$margen || '0'};
  width: ${p => p.$w || '620px'};
  max-width: 100%;
  height: auto;
  object-fit: contain;
  ${p => p.$wTablet && `@media (max-width: 991px) and (min-width: 601px) { width: ${p.$wTablet}; }`}
  ${p => p.$wMobile && `@media (max-width: 600px) { width: ${p.$wMobile}; }`}
`;

const Titulo = styled.h1`
  margin: 0;
  color: ${p => p.$color || '#fff'};
  font-size: ${p => p.$size || '2rem'};
  font-weight: ${p => p.$weight || 700};
  line-height: 1.2;
  font-family: ${p => p.$font || 'inherit'};
  @media (max-width: 600px) { font-size: ${p => p.$sizeMobile || '1.4rem'}; }
`;

const Subtitulo = styled.p`
  margin: 0;
  color: ${p => p.$color || 'rgba(255,255,255,0.9)'};
  font-size: ${p => p.$size || '1.1rem'};
  font-weight: ${p => p.$weight || 400};
  line-height: 1.5;
  max-width: 780px;
  font-family: ${p => p.$font || 'inherit'};
  @media (max-width: 600px) { font-size: ${p => p.$sizeMobile || '0.95rem'}; }
`;

/* Franja degradada al pie (o al tope) del banner: el remate de color del diseño. */
const Linea = styled.div`
  position: absolute;
  left: 0; right: 0;
  ${p => p.$arriba ? 'top: 0;' : 'bottom: 0;'}
  height: ${p => p.$alto || '6px'};
  background: ${p => p.$bg};
  z-index: 3;
`;

const ALIGN_MAP = { izquierda: 'flex-start', centro: 'center', derecha: 'flex-end' };
const TEXT_MAP = { izquierda: 'left', centro: 'center', derecha: 'right' };

const resolveUrl = (v) => {
  if (!v) return null;
  return v.startsWith('http') ? v : `${BACKEND_BASE}/${v.replace(/^\//, '')}`;
};

const BannerLogoSection = ({ config, openLogin, openRegister, tl }) => {
  const c = config || {};

  const fondoUrl = resolveUrl(c.imagen_fondo);
  const logoUrl = resolveUrl(c.logo);

  const alto = resolveDeviceValue(c.altura, '');
  const padding = resolveDeviceValue(c.padding, '');
  const logoW = resolveDeviceValue(c.logo_ancho, '');

  const alignH = ALIGN_MAP[c.alineacion || 'centro'] || 'center';
  const textAlign = TEXT_MAP[c.alineacion || 'centro'] || 'center';

  const titulo = tl ? tl(c.titulo) : c.titulo;
  const subtitulo = tl ? tl(c.subtitulo) : c.subtitulo;

  const logo = logoUrl && (
    <LogoImg
      src={logoUrl}
      alt={c.logo_alt || ''}
      $margen={c.logo_margen}
      $w={logoW.desktop || undefined}
      $wTablet={logoW.tablet || undefined}
      $wMobile={logoW.mobile || undefined}
    />
  );

  // El logo puede llevar a una ruta interna, a una URL externa o abrir el modal
  // de acceso; sin `logo_link` no es clicable.
  const envolverLogo = () => {
    if (!logo) return null;
    const link = (c.logo_link || '').trim();
    if (!link) return logo;
    if (link === 'login') return <span role="button" style={{ cursor: 'pointer' }} onClick={() => openLogin?.()}>{logo}</span>;
    if (link === 'registro') return <span role="button" style={{ cursor: 'pointer' }} onClick={() => openRegister?.()}>{logo}</span>;
    if (/^https?:\/\//i.test(link)) return <a href={link} target="_blank" rel="noopener noreferrer">{logo}</a>;
    return <Link to={link}>{logo}</Link>;
  };

  return (
    <Banner
      $alto={alto.desktop || '380px'}
      $altoTablet={alto.tablet}
      $altoMobile={alto.mobile}
      $padding={padding.desktop}
      $paddingTablet={padding.tablet}
      $paddingMobile={padding.mobile}
      $alignH={alignH}
      $alignV={ALIGN_MAP[c.alineacion_vertical || 'centro'] || 'center'}
      style={c.color_fondo ? { background: c.color_fondo } : undefined}
    >
      {fondoUrl && (
        <Fondo
          $url={fondoUrl}
          $size={c.tamano_fondo}
          $pos={c.posicion_fondo}
          $parallax={c.parallax === true}
        />
      )}
      {c.overlay && <Velo $overlay={c.overlay} $mix={c.overlay_mezcla} />}

      <Contenido
        $max={c.ancho_contenido}
        $alignH={alignH}
        $textAlign={textAlign}
        $gap={c.espaciado}
      >
        {/* `titulo_arriba` invierte el orden para el caso "rótulo + logo"
            (p. ej. una franja de avales: la palabra AVAL sobre el sello). */}
        {c.titulo_arriba !== true && envolverLogo()}
        {titulo && (
          <Titulo
            $color={c.color_titulo}
            $size={c.tamano_titulo}
            $sizeMobile={c.tamano_titulo_movil}
            $weight={c.peso_titulo}
            $font={c.fuente_titulo}
          >
            {titulo}
          </Titulo>
        )}
        {subtitulo && (
          <Subtitulo
            $color={c.color_subtitulo}
            $size={c.tamano_subtitulo}
            $sizeMobile={c.tamano_subtitulo_movil}
            $weight={c.peso_subtitulo}
            $font={c.fuente_subtitulo}
          >
            {subtitulo}
          </Subtitulo>
        )}
        {c.titulo_arriba === true && envolverLogo()}
      </Contenido>

      {c.linea_superior && <Linea $arriba $bg={c.linea_superior} $alto={c.linea_superior_alto} />}
      {c.linea_inferior && <Linea $bg={c.linea_inferior} $alto={c.linea_inferior_alto} />}
    </Banner>
  );
};

export default BannerLogoSection;
